/** sessionStorage: dev plane active after /api/miniapp/dev-config succeeds. */
import { miniappFetch } from '../api/miniappApiClient';

export const MINIAPP_DEV_PLANE_FLAG = 'jester_local_dev_plane';
export const MINIAPP_DEV_TELEGRAM_ID_KEY = 'jester_dev_telegram_id';

function readCachedDevId(): number | null {
  try {
    const direct = sessionStorage.getItem(MINIAPP_DEV_TELEGRAM_ID_KEY);
    if (direct && /^\d+$/.test(direct)) return parseInt(direct, 10);
  } catch {}
  return null;
}

/** True when the page URL looks like local dev (browser, Vite, LAN). */
export function isLocalMiniappHost(): boolean {
  try {
    const host = window.location.hostname.toLowerCase();
    if (!host) return false;
    if (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host === '0.0.0.0' ||
      host.endsWith('.local')
    ) {
      return true;
    }
    if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
    if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
    if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
    return false;
  } catch {
    return false;
  }
}

export function markMiniappDevPlaneActive(adminId: number): void {
  try {
    sessionStorage.setItem(MINIAPP_DEV_PLANE_FLAG, '1');
    sessionStorage.setItem(MINIAPP_DEV_TELEGRAM_ID_KEY, String(adminId));
  } catch {}
}

export function clearMiniappDevPlaneActive(): void {
  try {
    sessionStorage.removeItem(MINIAPP_DEV_PLANE_FLAG);
    sessionStorage.removeItem(MINIAPP_DEV_TELEGRAM_ID_KEY);
  } catch {}
}

/**
 * True when local ADMIN_RIG dev plane is active (hostname, cached admin id, or dev-config probe).
 * Use for auth bypasses — Cursor's embedded browser may not use a localhost hostname.
 */
export function isMiniappLocalDevActive(): boolean {
  if (isLocalMiniappHost()) return true;
  try {
    if (sessionStorage.getItem(MINIAPP_DEV_PLANE_FLAG) === '1') return true;
  } catch {}
  return readCachedDevId() != null;
}

let devPlaneProbeInflight: Promise<boolean> | null = null;

/** Calls GET /api/miniapp/dev-config (loopback-only on server) and caches ADMIN_ID. */
export function probeAndActivateMiniappDevPlane(): Promise<boolean> {
  if (isMiniappLocalDevActive() && readCachedDevId() != null) {
    return Promise.resolve(true);
  }
  if (devPlaneProbeInflight) return devPlaneProbeInflight;

  devPlaneProbeInflight = (async () => {
    try {
      const resp = await miniappFetch('/api/miniapp/dev-config', { method: 'GET' });
      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json?.success && json?.devMode && json?.adminId && Number.isFinite(json.adminId)) {
        markMiniappDevPlaneActive(Number(json.adminId));
        return true;
      }
    } catch {
      /* API not reachable */
    }
    return false;
  })().finally(() => {
    devPlaneProbeInflight = null;
  });

  return devPlaneProbeInflight;
}

/** Cached ADMIN_ID from dev-config probe (local dev only). */
export function getCachedDevTelegramId(): number | null {
  return readCachedDevId();
}

/** @deprecated Prefer {@link getCachedDevTelegramId} */
export const getCachedDevPlaneTelegramId = getCachedDevTelegramId;
export function buildDevPlaneTelegramUser(telegramId: number) {
  return { id: telegramId, first_name: 'Admin', username: 'admin' as const };
}

/** Resolve cached dev Telegram id, probing dev-config on localhost when needed. */
export async function ensureLocalDevTelegramId(): Promise<number | null> {
  const cached = readCachedDevId();
  if (cached != null) return cached;
  if (!isLocalMiniappHost()) return null;
  await probeAndActivateMiniappDevPlane();
  return readCachedDevId();
}
